import { useEffect, useState } from 'react';
import { usersAPI, portalAPI } from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Users, Shield, Mail, Search, Filter, Globe, Key, RefreshCw, Plus, UserPlus, Eye, EyeOff, Trash2, UserX, Clock, AlertTriangle } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import api from '../api';

export const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'crm' or 'portal'
  const [portalUsers, setPortalUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('crm');
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create User Dialog
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'sales_rep',
    phone: '',
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
    fetchPortalUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortalUsers = async () => {
    try {
      const response = await api.get('/portal/users');
      setPortalUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch portal users');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersAPI.updateRole(userId, newRole);
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleCreateUser = async () => {
    // Validation
    if (!newUser.email || !newUser.password || !newUser.first_name || !newUser.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (newUser.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (!newUser.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setCreateUserLoading(true);
    try {
      const response = await usersAPI.create(newUser);
      toast.success(`User ${newUser.email} created successfully!`);
      setShowCreateUserDialog(false);
      setNewUser({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'sales_rep',
        phone: '',
        is_active: true
      });
      fetchUsers();
    } catch (error) {
      console.error('Create user error:', error.response?.data || error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create user';
      toast.error(errorMsg);
    } finally {
      setCreateUserLoading(false);
    }
  };

  const handleResetPortalPassword = async (contactId, email) => {
    const newPassword = prompt(`Enter new password for ${email}:\n(Password must be at least 6 characters)`);
    if (!newPassword) {
      toast.info('Password reset cancelled');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await portalAPI.adminResetPassword({ contact_id: contactId, new_password: newPassword });
      toast.success(`Password reset for ${email}. New password: ${newPassword}`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleResetCRMPassword = async (userId, email) => {
    const newPassword = prompt(`Enter new password for ${email}:\n(Password must be at least 8 characters)`);
    if (!newPassword) {
      toast.info('Password reset cancelled');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      await usersAPI.adminResetPassword(userId, newPassword);
      toast.success(`Password reset for ${email}. New password: ${newPassword}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const confirmDeleteUser = (user, type) => {
    setUserToDelete(user);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete || !deleteType) return;
    
    try {
      if (deleteType === 'crm') {
        await usersAPI.delete(userToDelete.id);
        toast.success(`CRM user ${userToDelete.email} deleted`);
        fetchUsers();
      } else {
        await portalAPI.deleteUser(userToDelete.id);
        toast.success(`Portal user ${userToDelete.email} deleted`);
        fetchPortalUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${deleteType} user`);
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setDeleteType(null);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus, type) => {
    try {
      if (type === 'crm') {
        await usersAPI.updateStatus(userId, !currentStatus);
        toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
        fetchUsers();
      } else {
        await portalAPI.updateUserStatus(userId, !currentStatus);
        toast.success(`Portal user ${currentStatus ? 'deactivated' : 'activated'}`);
        fetchPortalUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const roleLabels = {
    admin: 'Administrator',
    sales_rep: 'Sales Rep',
    sales_manager: 'Sales Manager',
    marketing_manager: 'Marketing',
    support_agent: 'Support',
    technical_support: 'Tech Support',
    hr_manager: 'HR Manager',
    finance_manager: 'Finance',
    customer: 'Customer'
  };

  const roleColors = {
    admin: 'bg-red-100 text-red-700',
    sales_rep: 'bg-blue-100 text-blue-700',
    sales_manager: 'bg-indigo-100 text-indigo-700',
    marketing_manager: 'bg-purple-100 text-purple-700',
    support_agent: 'bg-emerald-100 text-emerald-700',
    technical_support: 'bg-amber-100 text-amber-700',
    hr_manager: 'bg-pink-100 text-pink-700',
    finance_manager: 'bg-green-100 text-green-700',
    customer: 'bg-slate-100 text-slate-600'
  };

  // Filter users based on role and search
  const filteredUsers = users.filter(user => {
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesSearch = searchQuery === '' || 
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesRole && matchesSearch;
  });

  // Filter portal users based on search
  const filteredPortalUsers = portalUsers.filter(user => {
    return searchQuery === '' || 
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Group users by role for stats
  const usersByRole = users.reduce((acc, user) => {
    acc[user.role] = (acc[user.role] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">User Management</h1>
          <p className="text-muted-foreground mt-1">Manage CRM users and customer portal access</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchUsers(); fetchPortalUsers(); }}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button onClick={() => setShowCreateUserDialog(true)} data-testid="add-user-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Add New User
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className={`cursor-pointer transition-all ${roleFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setRoleFilter('all')}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">All Users</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
              <Users className="h-5 w-5 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        {Object.entries(roleLabels).slice(0, 6).map(([role, label]) => (
          <Card key={role} 
                className={`cursor-pointer transition-all ${roleFilter === role ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setRoleFilter(role)}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground truncate">{label}</p>
                  <p className="text-2xl font-bold">{usersByRole[role] || 0}</p>
                </div>
                <Shield className={`h-5 w-5 ${roleColors[role]?.replace('bg-', 'text-').replace('-100', '-500') || 'text-slate-400'}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="crm" className="flex items-center gap-2" data-testid="crm-users-tab">
              <Users className="h-4 w-4" /> CRM Users ({filteredUsers.length})
            </TabsTrigger>
            <TabsTrigger value="portal" className="flex items-center gap-2" data-testid="portal-users-tab">
              <Globe className="h-4 w-4" /> Portal Users ({portalUsers.length})
            </TabsTrigger>
          </TabsList>
          
          {/* Search */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
                data-testid="user-search-input"
              />
            </div>
            {activeTab === 'crm' && (
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-40" data-testid="role-filter-select">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {Object.entries(roleLabels).map(([role, label]) => (
                    <SelectItem key={role} value={role}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* CRM Users Tab */}
        <TabsContent value="crm">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5" />
                CRM Users
              </CardTitle>
              <CardDescription>
                {roleFilter !== 'all' ? `Showing ${roleLabels[roleFilter]} users` : 'All system users with CRM access'}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarFallback className="bg-slate-100 text-slate-600">
                                {getInitials(user.first_name, user.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{user.first_name} {user.last_name}</p>
                              {user.phone && (
                                <p className="text-xs text-muted-foreground">{user.phone}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            {user.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={user.role}
                            onValueChange={(v) => handleRoleChange(user.id, v)}
                          >
                            <SelectTrigger className="h-8 w-[140px]">
                              <Badge className={roleColors[user.role]}>
                                {roleLabels[user.role] || user.role}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(roleLabels).map(([role, label]) => (
                                <SelectItem key={role} value={role}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge className={user.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            {user.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResetCRMPassword(user.id, user.email)}
                              title="Reset password"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleUserStatus(user.id, user.is_active !== false, 'crm')}
                              title={user.is_active !== false ? 'Deactivate user' : 'Activate user'}
                            >
                              <UserX className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => confirmDeleteUser(user, 'crm')}
                              title="Delete user"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No users found matching your filters
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Portal Users Tab */}
        <TabsContent value="portal">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Globe className="h-5 w-5 text-blue-500" />
                Customer Portal Users
              </CardTitle>
              <CardDescription>
                Customers with access to the self-service portal. Users inactive for 30+ days are marked.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {portalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Account Status</TableHead>
                      <TableHead>Activity</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPortalUsers.length > 0 ? (
                      filteredPortalUsers.map((user) => (
                        <TableRow key={user.id} data-testid={`portal-user-row-${user.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-9 w-9">
                                <AvatarFallback className="bg-blue-100 text-blue-600">
                                  {getInitials(user.first_name, user.last_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{user.first_name} {user.last_name}</p>
                                <p className="text-xs text-muted-foreground">Contact ID: {user.contact_id?.slice(0, 8)}...</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              {user.email}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={user.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                              {user.is_active !== false ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {user.activity_status === 'inactive' ? (
                              <Badge className="bg-amber-100 text-amber-700">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Inactive ({user.days_since_login}d)
                              </Badge>
                            ) : user.activity_status === 'never_logged_in' ? (
                              <Badge className="bg-slate-100 text-slate-600">
                                <Clock className="h-3 w-3 mr-1" />
                                Never logged in
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                Active
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {user.last_login ? (
                              <div>
                                <div>{format(new Date(user.last_login), 'MMM d, yyyy')}</div>
                                <div className="text-xs">{formatDistanceToNow(new Date(user.last_login), { addSuffix: true })}</div>
                              </div>
                            ) : 'Never'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleResetPortalPassword(user.contact_id, user.email)}
                                title="Reset password"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleUserStatus(user.id, user.is_active !== false, 'portal')}
                                title={user.is_active !== false ? 'Disable portal access' : 'Enable portal access'}
                              >
                                <UserX className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => confirmDeleteUser(user, 'portal')}
                                title="Delete portal user"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          {searchQuery ? 'No portal users found matching your search' : 'No customer portal users yet'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteType === 'crm' ? 'CRM' : 'Portal'} User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.email}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-600" />
              Add New User
            </DialogTitle>
            <DialogDescription>
              Create a new CRM user account. Users will receive their credentials via email.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                  placeholder="John"
                  data-testid="new-user-first-name"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                  placeholder="Doe"
                  data-testid="new-user-last-name"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="john.doe@company.com"
                data-testid="new-user-email"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  placeholder="Minimum 8 characters"
                  data-testid="new-user-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Must be at least 8 characters</p>
            </div>
            
            <div>
              <Label htmlFor="role">Role *</Label>
              <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-red-100 text-red-700 text-xs">Admin</Badge>
                      <span>Administrator</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="sales_manager">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-indigo-100 text-indigo-700 text-xs">Manager</Badge>
                      <span>Sales Manager</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="hr_manager">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-pink-100 text-pink-700 text-xs">Manager</Badge>
                      <span>HR Manager</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="marketing_manager">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-100 text-purple-700 text-xs">Manager</Badge>
                      <span>Marketing Manager</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="finance_manager">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700 text-xs">Manager</Badge>
                      <span>Finance Manager</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="sales_rep">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700 text-xs">Staff</Badge>
                      <span>Sales Representative</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="support_agent">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">Staff</Badge>
                      <span>Support Agent</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="technical_support">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-100 text-amber-700 text-xs">Staff</Badge>
                      <span>Technical Support</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={newUser.phone}
                onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                placeholder="+1 (555) 123-4567"
                data-testid="new-user-phone"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUserDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser}
              disabled={createUserLoading || !newUser.email || !newUser.password || !newUser.first_name || !newUser.last_name}
              data-testid="create-user-submit"
            >
              {createUserLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
